import { TypedDocumentNode, gql } from '@apollo/client';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import Dropdown from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FixtureStatus as CoreFixtureStatus } from '@sorare/core/src/components/lobby/FixtureStatus';

import { getFixtureState, hasGamesStarted } from 'lib/so5';

import { FixtureTimeline } from '../FixtureTimeline';
import {
  FixtureStatus_so5Fixture,
  FixtureStatus_so5Lineup,
} from './__generated__/index.graphql';

type Props = {
  so5Fixture: FixtureStatus_so5Fixture;
  so5Lineup?: FixtureStatus_so5Lineup;
  onClick?: () => void;
  detailsOpen?: boolean;
  setDetailsOpen?: (value: boolean) => void;
};

export const FixtureStatus = ({
  setDetailsOpen,
  detailsOpen,
  so5Fixture,
  so5Lineup,
  onClick,
}: Props) => {
  const fixtureState = getFixtureState(so5Fixture);
  const hasStarted = so5Lineup ? hasGamesStarted(so5Lineup) : true;

  if (!fixtureState) {
    return null;
  }

  return (
    <Dropdown
      align="right"
      open={detailsOpen}
      onOpen={() => {
        setDetailsOpen?.(true);
      }}
      label={({ onClick: onDropdownClick, ...props }) => (
        <ButtonBase
          {...props}
          onClick={e => {
            onDropdownClick?.(e);
            setDetailsOpen?.(true);
            onClick?.();
          }}
        >
          <CoreFixtureStatus
            fixtureState={fixtureState}
            hasGamesStarted={hasStarted}
            startDate={so5Fixture.startDate}
          />
        </ButtonBase>
      )}
      onClose={() => setDetailsOpen?.(false)}
    >
      <FixtureTimeline so5Fixture={so5Fixture} so5Lineup={so5Lineup} />
    </Dropdown>
  );
};

FixtureStatus.fragments = {
  so5Fixture: gql`
    fragment FixtureStatus_so5Fixture on So5Fixture {
      slug
      startDate
      ...getFixtureState_so5Fixture
      ...FixtureTimeline_so5Fixture
    }
    ${getFixtureState.fragments.so5Fixture}
    ${FixtureTimeline.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureStatus_so5Fixture>,
  so5Lineup: gql`
    fragment FixtureStatus_so5Lineup on So5Lineup {
      id
      ...FixtureTimeline_so5Lineup
      ...hasGamesStarted_so5Lineup
    }
    ${FixtureTimeline.fragments.so5Lineup}
    ${hasGamesStarted.fragments.so5Lineup}
  ` as TypedDocumentNode<FixtureStatus_so5Lineup>,
};
